import { Icon } from '@statsbomb/kitbag-components';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { PlayerLink, TeamLinkTable } from '@/components/tables/TableLink';
import { isLinkObject, isLinkType } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { translateHyphenatedValue, translateValue } from '@/utils/table';
import { useDecimalPlaces } from './useDecimalPlaces';

const linkMap = {
  teamLink: TeamLinkTable,
  playerLink: PlayerLink,
  recipientPlayerLink: PlayerLink,
  currentTeam: TeamLinkTable,
};

const chooseLinkComponent = (value: NestedObject[string], columnKey: string) => {
  if (!isLinkObject(value) || !isLinkType(columnKey)) return null;

  const LinkComponent = linkMap[columnKey];

  const { id, label } = value;
  return <LinkComponent id={id} label={label} />;
};

export const useCellValue = (
  value: NestedObject[string],
  formatRule: string | undefined,
  columnKey: string,
  t?: TFunction,
) => {
  const formatNumberDecimals = useDecimalPlaces();
  const wholeNumberFormat = (value: number) => formatNumberDecimals(0, value);
  const twoDecimalsNumberFormat = (value: number) => formatNumberDecimals(2, value);

  switch (formatRule) {
    case 'string':
      return value || null;
    case 'string-translate':
      return translateValue(columnKey, value, t);
    case 'string-hyphen':
      return translateHyphenatedValue(columnKey, value, t);
    case 'integer':
      return typeof value === 'number' ? wholeNumberFormat(value) : null;
    case 'percentage':
      return typeof value === 'number' ? wholeNumberFormat(value * 100) : null;
    case 'float-2':
      return typeof value === 'number' ? twoDecimalsNumberFormat(value) : null;
    case 'timestamptz':
      return dayjs(String(value)).isValid() ? dayjs(String(value)).format('YYYY-MM-DD HH:mm:ss') : null;
    case 'duration-m':
    case 'duration-s':
      return dayjs(Number(value)).isValid() ? dayjs(Number(value)).format('mm:ss:SSS') : null;
    case 'boolean':
      return value ? (
        <span className="flex justify-center" data-testid="formatted-boolean-icon">
          <Icon variant="TickDone" size="small" />
        </span>
      ) : null;
    case 'link':
      return chooseLinkComponent(value, columnKey);
    default:
      return value?.toString() || '';
  }
};
